import React, { useEffect, useState } from 'react';
import { Heart, Diamond, Gift, Clock, Sparkles, Star } from 'lucide-react';
import { MaschaSkinsData } from './MaschaSkinsData';
interface MaschaHeroBannerProps {
  onTeasePreview: (skinId: string) => void;
  onSpinWheel: () => void;
  userTokens: {
    loveTokens: number;
    desireGems: number;
  };
}
export const MaschaHeroBanner: React.FC<MaschaHeroBannerProps> = ({
  onTeasePreview,
  onSpinWheel,
  userTokens
}) => {
  const [currentSkinIndex, setCurrentSkinIndex] = useState(0);
  const [isShimmering, setIsShimmering] = useState(false);
  // Auto-scroll through skins
  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentSkinIndex(prevIndex => (prevIndex + 1) % MaschaSkinsData.length);
    }, 5000);
    return () => clearInterval(interval);
  }, []);
  // Shimmer effect
  useEffect(() => {
    const shimmerInterval = setInterval(() => {
      setIsShimmering(true);
      setTimeout(() => setIsShimmering(false), 1500);
    }, 7000);
    return () => clearInterval(shimmerInterval);
  }, []);
  const currentSkin = MaschaSkinsData[currentSkinIndex];
  const unlockedSkins = MaschaSkinsData.filter(skin => skin.status !== 'Locked').length;
  const totalSkins = MaschaSkinsData.length;
  return <div className="relative w-full overflow-hidden rounded-xl mb-8" data-id="element-9411">
      {/* Background gradient overlay */}
      <div className="absolute inset-0 bg-gradient-to-b from-black/10 via-black/40 to-black/80 z-10" data-id="element-9412"></div>
      {/* Shimmer effect */}
      <div className={`absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent z-20 transform -translate-x-full transition-transform duration-1500 ease-in-out ${isShimmering ? 'translate-x-full' : '-translate-x-full'}`} data-id="element-9413"></div>
      {/* Hero image */}
      <div className="relative w-full h-[500px] overflow-hidden" data-id="element-9414">
        <img src={currentSkin.preview_image_url} alt={currentSkin.skin_name} className="w-full h-full object-cover object-top transition-transform duration-700 ease-in-out hover:scale-105" data-id="element-9415" />
      </div>
      {/* Content overlay */}
      <div className="absolute inset-0 flex flex-col justify-end z-30 p-6 md:p-8" data-id="element-9416">
        <div className="bg-black/60 backdrop-blur-sm rounded-xl p-4 md:p-6 border border-gold/30" data-id="element-9417">
          <div className="flex items-center gap-2 mb-2" data-id="element-9418">
            <div className="w-10 h-10 rounded-full bg-gradient-to-r from-red-500 to-amber-500 flex items-center justify-center" data-id="element-9419">
              <span className="text-white font-bold" data-id="element-9420">M</span>
            </div>
            <div data-id="element-9421">
              <h2 className="text-xl md:text-2xl font-bold text-white" data-id="element-9422">
                Mascha
              </h2>
              <p className="text-amber-300 text-sm" data-id="element-9423">Premium Angel</p>
            </div>
            <div className="ml-auto" data-id="element-9424">
              <span className="px-3 py-1 bg-gradient-to-r from-amber-600 to-amber-800 rounded-full text-xs text-white flex items-center" data-id="element-9425">
                <Star size={12} className="mr-1" fill="white" data-id="element-9426" />
                <span data-id="element-9427">NEW</span>
              </span>
            </div>
          </div>
          <p className="text-gray-200 text-sm md:text-base mb-4" data-id="element-9428">
            Unleash your fantasies with Mascha, our seductive red-haired angel
            with captivating emerald eyes. Discover her five exclusive personas,
            each revealing a piece of her intimate story.
          </p>
          <div className="grid grid-cols-2 gap-4 mb-4" data-id="element-9429">
            <div className="bg-black/40 rounded-lg p-3 border border-gold/20" data-id="element-9430">
              <h3 className="text-amber-300 text-sm font-medium mb-1" data-id="element-9431">
                Skin Collection
              </h3>
              <div className="flex items-center justify-between" data-id="element-9432">
                <span className="text-white" data-id="element-9433">
                  {unlockedSkins}/{totalSkins} Unlocked
                </span>
                <div className="h-1.5 w-24 bg-gray-700 rounded-full overflow-hidden" data-id="element-9434">
                  <div className="h-full bg-gradient-to-r from-amber-500 to-amber-300" style={{
                  width: `${unlockedSkins / totalSkins * 100}%`
                }} data-id="element-9435"></div>
                </div>
              </div>
            </div>
            <div className="bg-black/40 rounded-lg p-3 border border-gold/20" data-id="element-9436">
              <h3 className="text-amber-300 text-sm font-medium mb-1" data-id="element-9437">
                Storyline Progress
              </h3>
              <div className="flex items-center justify-between" data-id="element-9438">
                <span className="text-white" data-id="element-9439">
                  {unlockedSkins > 0 ? Math.floor(unlockedSkins / totalSkins * 100) : 0}
                  % Complete
                </span>
                <div className="h-1.5 w-24 bg-gray-700 rounded-full overflow-hidden" data-id="element-9440">
                  <div className="h-full bg-gradient-to-r from-pink-500 to-purple-500" style={{
                  width: `${unlockedSkins / totalSkins * 100}%`
                }} data-id="element-9441"></div>
                </div>
              </div>
            </div>
          </div>
          <div className="flex flex-wrap gap-2 mb-4" data-id="element-9442">
            {MaschaSkinsData.map((skin, index) => <div key={skin.id} className={`w-8 h-8 rounded-full overflow-hidden border-2 ${index === currentSkinIndex ? 'border-amber-400 scale-110' : 'border-gray-600 opacity-70'} transition-all duration-300`} data-id="element-9443">
                <img src={skin.preview_image_url} alt={skin.skin_name} className="w-full h-full object-cover object-top" data-id="element-9444" />
              </div>)}
          </div>
          <div className="flex flex-wrap gap-3 mt-4" data-id="element-9445">
            <button onClick={() => onTeasePreview(currentSkin.id)} className="px-4 py-2 bg-gradient-to-r from-amber-600 to-red-600 rounded-lg text-white text-sm flex items-center hover:shadow-lg hover:shadow-amber-500/20 transition-all duration-300" data-id="element-9446">
              <Sparkles size={16} className="mr-2" data-id="element-9447" />
              Tease Preview
            </button>
            <button onClick={onSpinWheel} className="px-4 py-2 bg-gradient-to-r from-purple-600 to-pink-600 rounded-lg text-white text-sm flex items-center animate-pulse hover:animate-none hover:shadow-lg hover:shadow-pink-500/20 transition-all duration-300" data-id="element-9448">
              <Gift size={16} className="mr-2" data-id="element-9449" />
              Spin to Win
            </button>
            {unlockedSkins === totalSkins ? <button className="px-4 py-2 bg-gradient-to-r from-green-600 to-teal-600 rounded-lg text-white text-sm flex items-center hover:shadow-lg hover:shadow-green-500/20 transition-all duration-300" data-id="element-9450">
                <Star size={16} className="mr-2" fill="currentColor" data-id="element-9451" />
                Claim Bond Bonus
              </button> : <div className="px-4 py-2 bg-gradient-to-r from-gray-700 to-gray-800 rounded-lg text-gray-300 text-sm flex items-center" data-id="element-9452">
                <Diamond size={16} className="mr-2" data-id="element-9453" />
                <span data-id="element-9454">{userTokens.desireGems} Gems</span>
              </div>}
          </div>
        </div>
      </div>
    </div>;
};